/**
 * US equity-market calendar — expirations, holidays and early closes.
 *
 * Everything here is computed from the rules, not fetched. These dates are
 * defined by the exchange calendar rather than by market data, so a request
 * would be a round trip to learn something arithmetic already knows.
 *
 * All dates are handled as ISO `YYYY-MM-DD` strings in **Eastern** terms and
 * built with `Date.UTC`. Using a local `new Date(y, m, d)` would shift the day
 * for anyone west of UTC and quietly hand back the wrong Friday.
 *
 * "Today" comes from `todayEt()` in utils/marketTime — never from a local
 * `new Date()`.
 */

export type EventKind =
  | 'monthly-opex'
  | 'quarterly-opex'
  | 'vix-expiry'
  | 'vix-weekly'
  | 'holiday'
  | 'early-close';

export interface MarketEvent {
  /** ISO date, YYYY-MM-DD. */
  date: string;
  kind: EventKind;
  /** Short label for the badge. */
  label: string;
  /** Longer text for a tooltip. */
  detail?: string;
}

/* ── date helpers ───────────────────────────────────────────────────── */

const DAY_MS = 86_400_000;

export function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function utc(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month, day));
}

/** Parses `YYYY-MM-DD` as a UTC midnight date. */
export function parseIso(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return utc(y, m - 1, d);
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * DAY_MS);
}

/** 0 = Sunday. */
export function weekday(d: Date): number {
  return d.getUTCDay();
}

/**
 * The nth given weekday of a month. `n` is 1-based.
 *
 * Does not validate that the nth occurrence exists — every month has at least
 * four of each weekday, and nothing here asks for a fifth.
 */
export function nthWeekday(year: number, month: number, day: number, n: number): Date {
  const first = utc(year, month, 1);
  const shift = (day - first.getUTCDay() + 7) % 7;
  return utc(year, month, 1 + shift + (n - 1) * 7);
}

/** The last given weekday of a month — Memorial Day is the only user. */
export function lastWeekday(year: number, month: number, day: number): Date {
  const last = utc(year, month + 1, 0);
  const shift = (last.getUTCDay() - day + 7) % 7;
  return addDays(last, -shift);
}

/**
 * Easter Sunday, Gregorian. Anonymous computus.
 *
 * Needed only for Good Friday, which is the one market holiday with no fixed
 * date and no nth-weekday rule.
 */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return utc(year, month - 1, day);
}

/**
 * The day a fixed-date holiday is observed.
 *
 * Saturday moves back to Friday, Sunday forward to Monday — the NYSE rule. A
 * July 4th on a Saturday closes the market on Friday the 3rd.
 *
 * New Year's Day is the exception and does not come through here on a
 * Saturday — see `holidays`.
 */
export function observed(d: Date): Date {
  const w = d.getUTCDay();
  if (w === 6) return addDays(d, -1);
  if (w === 0) return addDays(d, 1);
  return d;
}

/* ── expirations ────────────────────────────────────────────────────── */

/** Standard monthly equity expiration: the third Friday. */
export function monthlyOpex(year: number, month: number): Date {
  return nthWeekday(year, month, 5, 3);
}

/** March, June, September, December — the quad-witching months. */
export function isQuarterlyMonth(month: number): boolean {
  return month % 3 === 2;
}

/**
 * The scheduled VIX settlement date: the Wednesday **30 days before** the third
 * Friday of the FOLLOWING month.
 *
 * That is the CBOE rule verbatim, and it is why VIX settlement drifts around
 * the month rather than sitting on a fixed weekday-of-month.
 *
 * This is the unadjusted date. Use `vixSettlement` for the real one — the
 * exception below is not an edge case, it fires in 2026.
 */
export function vixExpiry(year: number, month: number): Date {
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  return addDays(monthlyOpex(nextYear, nextMonth), -30);
}

/**
 * VIX settlement with the CBOE exception applied.
 *
 * > Exchange holidays on Wednesdays and Fridays move the expiration to the
 * > preceding Tuesday.
 *
 * Two independent triggers, and the **second is the one that surprises people**:
 *
 * 1. The scheduled Wednesday is itself an exchange holiday.
 * 2. The third Friday it is measured against is an exchange holiday — that
 *    Friday's expiration moves back to the Thursday, so the 30-day count moves
 *    with it.
 *
 * Trigger 2 fires in **May 2026**: June's third Friday is the 19th, which is
 * Juneteenth, so VIX settles Tuesday May 19 rather than Wednesday the 20th.
 * Measuring from the unadjusted Friday puts it a day late, and nothing about
 * the May calendar hints at why.
 */
export function vixSettlement(year: number, month: number, closed: Set<string>): Date {
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  return vixFromFriday(monthlyOpex(nextYear, nextMonth), closed);
}

/**
 * The VIX date measured from a given Friday expiration, exception applied.
 *
 * `Friday − 30 days` is always a Wednesday (30 mod 7 = 2), which is why VIX
 * settles midweek at all. Standard settlement counts back from a **third**
 * Friday; a VIX Weekly counts back from any other one. Identical rule, so it
 * lives in one function — two copies would drift on the exception.
 */
export function vixFromFriday(targetFriday: Date, closed: Set<string>): Date {
  const scheduled = addDays(targetFriday, -30);
  if (!closed.has(iso(scheduled)) && !closed.has(iso(targetFriday))) return scheduled;

  // Preceding business day. A loop rather than a fixed -1 so a holiday
  // adjacent to a weekend cannot land settlement on a Saturday.
  let d = addDays(scheduled, -1);
  let guard = 0;
  while (!isTradingDay(d, closed) && guard++ < 10) d = addDays(d, -1);
  return d;
}

/** True when a date is the third Friday of its own month. */
export function isMonthlyOpex(d: Date): boolean {
  return iso(monthlyOpex(d.getUTCFullYear(), d.getUTCMonth())) === iso(d);
}

/* ── holidays ───────────────────────────────────────────────────────── */

export interface Holiday {
  date: Date;
  name: string;
  /** Closes at 1:00 pm ET rather than not opening at all. */
  early?: boolean;
}

/**
 * Every NYSE closure and early close in a calendar year.
 *
 * Early closes follow the exchange's own conditions: the day before
 * Independence Day only when the 4th is a weekday, the Friday after
 * Thanksgiving always, and Christmas Eve only when Christmas is a weekday.
 * Hard-coding them as fixed dates gets it wrong most years.
 */
export function holidays(year: number): Holiday[] {
  const out: Holiday[] = [
    { date: nthWeekday(year, 0, 1, 3), name: 'Martin Luther King Jr. Day' },
    { date: nthWeekday(year, 1, 1, 3), name: "Washington's Birthday" },
    { date: addDays(easterSunday(year), -2), name: 'Good Friday' },
    { date: lastWeekday(year, 4, 1), name: 'Memorial Day' },
    { date: observed(utc(year, 5, 19)), name: 'Juneteenth' },
    { date: observed(utc(year, 6, 4)), name: 'Independence Day' },
    { date: nthWeekday(year, 8, 1, 1), name: 'Labor Day' },
    { date: nthWeekday(year, 10, 4, 4), name: 'Thanksgiving' },
    { date: observed(utc(year, 11, 25)), name: 'Christmas Day' },
  ];

  // New Year's Day is the one holiday that is NOT observed when it falls on a
  // Saturday — the NYSE simply skips it rather than closing the previous
  // Friday, because that Friday is the last trading day of the prior year.
  //   "Because the holiday falls on Saturday, January 1, 2028, no New Year's
  //    Day holiday is observed."  — NYSE holiday schedule
  // Applying the generic Saturday rule here shut the market on 31 Dec 2027.
  const jan1 = utc(year, 0, 1);
  if (jan1.getUTCDay() !== 6) {
    out.push({ date: observed(jan1), name: "New Year's Day" });
  }

  // Independence Day eve — only when the 4th itself is a trading day, so a
  // 4th that lands on a weekend does not produce a stray half day.
  const july4 = utc(year, 6, 4);
  const j4w = july4.getUTCDay();
  if (j4w >= 2 && j4w <= 5) {
    out.push({ date: addDays(july4, -1), name: 'Independence Day eve', early: true });
  }

  // The Friday after Thanksgiving, every year.
  out.push({
    date: addDays(nthWeekday(year, 10, 4, 4), 1),
    name: 'Day after Thanksgiving',
    early: true,
  });

  // Christmas Eve — same condition as July 3rd.
  const xmas = utc(year, 11, 25);
  const xw = xmas.getUTCDay();
  if (xw >= 2 && xw <= 5) {
    out.push({ date: utc(year, 11, 24), name: 'Christmas Eve', early: true });
  }

  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/** ISO dates of full closures in a year — early closes are trading days. */
export function closureDates(year: number): Set<string> {
  return new Set(holidays(year).filter((h) => !h.early).map((h) => iso(h.date)));
}

/** Weekday and not a closure. */
export function isTradingDay(d: Date, closed: Set<string>): boolean {
  const w = d.getUTCDay();
  return w >= 1 && w <= 5 && !closed.has(iso(d));
}

/**
 * The first day of a window of `sessions` trading days ending at `fromIso`.
 *
 * ⚠️ **Trading sessions, not calendar days.** "Last 5D" on a flow panel means
 * five sessions — counting calendar days instead makes a Monday view reach
 * back only to Wednesday, and a week containing a holiday quietly shows four
 * days of data under a label saying five.
 *
 * `fromIso` need not itself be a trading day: a Saturday walks back and the
 * window ends on the Friday, which is what a weekend reader should see.
 */
export function sessionWindowStart(sessions: number, fromIso: string): string {
  // The previous year too, so a January window reaching into December still
  // knows about Christmas.
  const year = Number(fromIso.slice(0, 4));
  const closed = new Set<string>();
  [year - 1, year].forEach((y) => closureDates(y).forEach((d) => closed.add(d)));

  let day = parseIso(fromIso);
  let found = 0;
  let start = fromIso;

  // Bounded: even a week of closures cannot push five sessions past this.
  for (let guard = 0; found < sessions && guard < 40; guard++) {
    if (isTradingDay(day, closed)) {
      found++;
      start = iso(day);
    }
    day = addDays(day, -1);
  }

  return start;
}

/* ── the session clock ──────────────────────────────────────────────── */

const OPEN_MIN = 9 * 60 + 30;
const CLOSE_MIN = 16 * 60;

/** The Eastern wall clock at a given instant: its date, and minutes past midnight. */
function etParts(now: Date): { dateIso: string; minutes: number } {
  // en-CA gives YYYY-MM-DD; the hour/minute come from the same instant so the
  // date and the clock cannot disagree across a midnight boundary.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

  return {
    dateIso: `${get('year')}-${get('month')}-${get('day')}`,
    // Midnight formats as "24" in some ICU versions rather than "00".
    minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')),
  };
}

/**
 * The instant at which the Eastern clock reads `minutesOfDay` on an ISO date.
 *
 * There is no way to build this directly — `Date` speaks UTC and local time and
 * nothing else. So: guess the UTC instant, read it back through the Eastern
 * calendar to learn that date's offset, and correct by the difference.
 *
 * One pass is exact for any time away from 02:00 local, which is where the DST
 * transitions sit. Everything here asks for 16:00.
 */
function etInstant(dateIso: string, minutesOfDay: number): Date {
  const hh = String(Math.floor(minutesOfDay / 60)).padStart(2, '0');
  const mm = String(minutesOfDay % 60).padStart(2, '0');
  const guess = new Date(`${dateIso}T${hh}:${mm}:00Z`);

  const back = etParts(guess);
  const drift =
    parseIso(back.dateIso).getTime() +
    back.minutes * 60_000 -
    (parseIso(dateIso).getTime() + minutesOfDay * 60_000);

  return new Date(guess.getTime() - drift);
}

/**
 * Whether the regular session is open right now.
 *
 * For deciding whether to keep polling. Uses the exchange's clock, not the
 * viewer's — a poll loop keyed on local hours runs all night for anyone
 * outside the Americas and stops at lunchtime for anyone in Asia.
 *
 * ⚠️ Early closes are treated as full days. A half day would be polled until
 * 16:00 rather than 13:00, which costs three extra requests against unchanged
 * data — cheaper than the alternative of getting the rule wrong and stopping
 * a normal session early.
 */
export function isMarketOpen(now: Date = new Date()): boolean {
  const { dateIso, minutes } = etParts(now);

  const day = parseIso(dateIso);
  if (!isTradingDay(day, closureDates(day.getUTCFullYear()))) return false;

  return minutes >= OPEN_MIN && minutes < CLOSE_MIN;
}

export interface SessionStamp {
  /** ISO date of the trading session the figures describe. */
  date: string;
  /** ISO instant they are effectively as of. */
  asOf: string;
}

/**
 * What a snapshot taken *now* is actually a snapshot **of**.
 *
 * ⚠️ Not the same as the time of the request, and that gap is the whole point.
 * A quote endpoint hit at 17:16 returns the 16:00 close; stamping it 17:16
 * asserts an hour of trading that did not happen. Worse before the bell: a
 * request at 08:00 Tuesday returns *Monday's* close, so both the time and the
 * date would be wrong.
 *
 * So:
 *   - mid-session  → now, which is genuinely what the tape says
 *   - after close  → 16:00 today
 *   - before open, weekend, holiday → 16:00 of the previous trading day
 *
 * Early closes are stamped 16:00 like everything else, matching the tolerance
 * `isMarketOpen` already takes. On the six half-days a year the label is three
 * hours generous; it is never wrong about which session.
 */
export function sessionAsOf(now: Date = new Date()): SessionStamp {
  const { dateIso, minutes } = etParts(now);

  // The previous year too: January mornings look back into December.
  const year = Number(dateIso.slice(0, 4));
  const closed = new Set<string>();
  [year - 1, year].forEach((y) => closureDates(y).forEach((d) => closed.add(d)));

  const today = parseIso(dateIso);

  if (isTradingDay(today, closed) && minutes >= OPEN_MIN) {
    return {
      date: dateIso,
      asOf:
        minutes < CLOSE_MIN
          ? now.toISOString()
          : etInstant(dateIso, CLOSE_MIN).toISOString(),
    };
  }

  let d = addDays(today, -1);
  let guard = 0;
  while (!isTradingDay(d, closed) && guard++ < 10) d = addDays(d, -1);

  const prev = iso(d);
  return { date: prev, asOf: etInstant(prev, CLOSE_MIN).toISOString() };
}

/* ── assembly ───────────────────────────────────────────────────────── */

/**
 * Every market event between two ISO dates, inclusive.
 *
 * Expirations that land on a closure are pulled back to the previous trading
 * day, which is what the exchange does — a Good Friday third Friday settles on
 * the Thursday.
 */
export function marketEvents(fromIso: string, toIso: string): MarketEvent[] {
  const from = parseIso(fromIso);
  const to = parseIso(toIso);
  if (to < from) return [];

  // A year either side, so an expiration pulled back across a boundary and a
  // VIX expiry reaching into the next month are both covered.
  const years: number[] = [];
  for (let y = from.getUTCFullYear() - 1; y <= to.getUTCFullYear() + 1; y++) years.push(y);

  const closed = new Set<string>();
  years.forEach((y) => closureDates(y).forEach((d) => closed.add(d)));

  /** Back up to the previous trading day, if the given one is closed. */
  const settle = (d: Date): Date => {
    let out = d;
    let guard = 0;
    while (!isTradingDay(out, closed) && guard++ < 10) out = addDays(out, -1);
    return out;
  };

  const events: MarketEvent[] = [];
  const push = (d: Date, kind: EventKind, label: string, detail?: string) => {
    const s = iso(d);
    if (s < fromIso || s > toIso) return;
    events.push({ date: s, kind, label, detail });
  };

  years.forEach((year) => {
    // Holidays and early closes.
    holidays(year).forEach((h) => {
      push(
        h.date,
        h.early ? 'early-close' : 'holiday',
        h.early ? 'Early close' : 'Closed',
        h.early ? `${h.name} — closes 1:00 pm ET` : h.name
      );
    });

    for (let month = 0; month < 12; month++) {
      const monthly = settle(monthlyOpex(year, month));
      if (isQuarterlyMonth(month)) {
        push(monthly, 'quarterly-opex', 'Quad witch',
          'Quarterly expiration — index futures, index options, stock options and single-stock futures');
      } else {
        push(monthly, 'monthly-opex', 'Monthly OpEx', 'Standard monthly equity and index expiration');
      }

    }

    // Walk every Friday of the year for the VIX dates they anchor.
    //
    // Weekly equity expirations are deliberately NOT emitted. There is one
    // every Friday, so badging them marked a fifth of the calendar with
    // something that is always true — it crowded out the dates that actually
    // differ from the ordinary week.
    let d = nthWeekday(year, 0, 5, 1);
    while (d.getUTCFullYear() === year) {
      // The VIX date this Friday anchors. Already exception-adjusted, so it
      // does not go through `settle`. A third Friday gives standard monthly
      // settlement; every other Friday gives a Weekly.
      //
      // Driven off the Friday rather than off the calendar month, because the
      // 30-day count crosses month and year boundaries — January's Fridays
      // produce VIX dates in the previous December.
      const vix = vixFromFriday(d, closed);
      const monthly = isMonthlyOpex(d);
      const moved = vix.getUTCDay() !== 3;
      push(
        vix,
        monthly ? 'vix-expiry' : 'vix-weekly',
        monthly ? 'VIX Monthly' : 'VIX Weekly',
        moved
          ? 'Moved off Wednesday: an exchange holiday falls on the settlement day or the Friday it is measured against'
          : monthly
            ? 'VIX futures and options settle — 30 days before the following month’s third Friday'
            : 'VIX Weeklys settle — 30 days before the corresponding weekly Friday expiration'
      );

      d = addDays(d, 7);
    }
  });

  return events.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Groups events by ISO date, preserving order within a day. */
export function byDate(events: MarketEvent[]): Map<string, MarketEvent[]> {
  const map = new Map<string, MarketEvent[]>();
  events.forEach((e) => {
    const list = map.get(e.date);
    if (list) list.push(e);
    else map.set(e.date, [e]);
  });
  return map;
}
